/**
 * Zeomart Marketplace Vanilla JS Motion & Interaction Engine
 * Handles Infinite 1-by-1 Marquee Carousels, Scroll Reveals, Entrance Sequences, Tabs, Countdowns, Accordions, Scroll Progress, Back-to-Top, and Ajax Cart
 */

(function () {
  'use strict';

  // ===== AJAX ADD TO CART HELPER WITH MOTION FEEDBACK =====
  window.zmAddToCart = async function (form) {
    if (!form || form.dataset.zmSubmitting === 'true') return;

    const submitBtn = form.querySelector('button[type="submit"]');
    const originalDisabled = submitBtn ? submitBtn.disabled : false;

    form.dataset.zmSubmitting = 'true';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.classList.add('loading');
      submitBtn.setAttribute('aria-busy', 'true');
    }

    try {
      const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

      const response = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });

      const addedItem = await response.json();

      if (!response.ok) {
        throw new Error(addedItem.description || addedItem.message || 'Unable to add this product to your cart.');
      }

      const cartResponse = await fetch(root + 'cart.js', {
        headers: { Accept: 'application/json' }
      });

      if (!cartResponse.ok) {
        throw new Error('The product was added, but the cart count could not be refreshed. Open your cart to verify.');
      }

      const cart = await cartResponse.json();

      // Update the custom Zeomart cart badge.
      document.querySelectorAll('.zm-tool-badge').forEach((badge) => {
        badge.textContent = cart.item_count;
        badge.classList.remove('popping');
        void badge.offsetWidth;
        badge.classList.add('popping');
        window.setTimeout(() => badge.classList.remove('popping'), 500);
      });

      // Update common Shopify cart-count elements, if present.
      document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
        bubble.classList.toggle('hidden', cart.item_count === 0);
        const spans = bubble.querySelectorAll('span');
        if (spans.length) {
          spans[0].textContent = cart.item_count;
          if (spans[1]) spans[1].textContent = cart.item_count === 1 ? 'item' : 'items';
        }
      });

      document.querySelectorAll('[data-cart-count]').forEach((element) => {
        element.textContent = cart.item_count;
      });

      // Let theme components listen for cart updates without forcing a redirect.
      document.dispatchEvent(new CustomEvent('zm:cart-updated', {
        detail: { cart: cart, addedItem: addedItem }
      }));

      if (submitBtn) {
        submitBtn.classList.remove('loading');
        submitBtn.classList.add('added');
        submitBtn.setAttribute('aria-label', 'Added to cart');
        window.setTimeout(() => {
          submitBtn.classList.remove('added');
          submitBtn.removeAttribute('aria-busy');
        }, 1600);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      if (submitBtn) {
        submitBtn.classList.remove('loading');
        submitBtn.removeAttribute('aria-busy');
      }
      window.alert(error.message || 'Could not add the product. Please try again.');
    } finally {
      form.dataset.zmSubmitting = 'false';
      if (submitBtn) {
        submitBtn.disabled = originalDisabled;
      }
    }
  };

  // ===== UNIFIED SCROLL REVEAL (INTERSECTION OBSERVER) =====
  let revealObserver;
  function initScrollReveal() {
    if (typeof IntersectionObserver === 'undefined') {
      document.querySelectorAll('.zm-reveal-up, .zm-reveal-fade, .zm-reveal-scale, .zm-reveal-left, .zm-reveal-right, .zm-stagger').forEach((el) => {
        el.classList.add('is-visible');
      });
      return;
    }

    if (revealObserver) {
      revealObserver.disconnect();
    }

    revealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    document.querySelectorAll('.zm-reveal-up, .zm-reveal-fade, .zm-reveal-scale, .zm-reveal-left, .zm-reveal-right, .zm-stagger').forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        el.classList.add('is-visible');
      } else {
        revealObserver.observe(el);
      }
    });
  }

  // ===== SCROLL PROGRESS & BACK TO TOP BUTTON =====
  function initScrollProgressAndBackToTop() {
    const progressBar = document.querySelector('.zm-scroll-progress');
    let backToTopBtn = document.querySelector('.zm-back-to-top');

    if (!backToTopBtn) {
      backToTopBtn = document.createElement('button');
      backToTopBtn.className = 'zm-back-to-top';
      backToTopBtn.setAttribute('aria-label', 'Back to top');
      backToTopBtn.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <polyline points="18 15 12 9 6 15"></polyline>
        </svg>
      `;
      document.body.appendChild(backToTopBtn);

      backToTopBtn.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollTop = window.scrollY || document.documentElement.scrollTop;
          const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
          const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;

          if (progressBar) {
            progressBar.style.width = scrollPercent + '%';
          }

          if (backToTopBtn) {
            if (scrollTop > 350) {
              backToTopBtn.classList.add('visible');
            } else {
              backToTopBtn.classList.remove('visible');
            }
          }

          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  // ===== INFINITE 1-BY-1 MARQUEE CAROUSEL ENGINE =====
  function initCarousel(wrapper) {
    if (!wrapper) return;

    // If already initialized, just ensure it's active
    if (wrapper.__zmCarouselInit) {
      if (typeof wrapper.__zmResume === 'function') {
        wrapper.__zmResume();
      }
      return;
    }
    wrapper.__zmCarouselInit = true;

    const trackContainer = wrapper.querySelector('.zm-carousel-track-container');
    const track = wrapper.querySelector('.zm-carousel-track');
    const initialSlides = wrapper.querySelectorAll('.zm-carousel-slide');
    const prevBtn = wrapper.querySelector('.zm-carousel-arrow--prev');
    const nextBtn = wrapper.querySelector('.zm-carousel-arrow--next');
    const dotsContainer = wrapper.querySelector('.zm-carousel-dots');

    if (!trackContainer || !track || initialSlides.length === 0) return;

    // Reset styles
    track.style.transform = 'translateX(0px)';
    track.style.transition = 'none';

    // Calculate dynamic gap
    function getGap() {
      try {
        const computed = parseFloat(window.getComputedStyle(track).gap || window.getComputedStyle(track).columnGap);
        if (!isNaN(computed) && computed >= 0) return computed;
      } catch (e) {}
      if (window.innerWidth >= 1024) return 20;
      if (window.innerWidth >= 768) return 16;
      return 12;
    }

    // Dynamic Step: width of 1 card + gap
    function getSingleStep() {
      const firstSlide = track.firstElementChild;
      if (!firstSlide) return 220;
      const rect = firstSlide.getBoundingClientRect();
      const slideWidth = rect.width > 0 ? rect.width : (firstSlide.offsetWidth || 220);
      const gap = getGap();
      return slideWidth + gap;
    }

    // ===== SLIDE CLONING FOR SEAMLESS INFINITE MARQUEE LOOP =====
    // Ensure track has at least 16 slides so loop never has empty space
    const originalCount = initialSlides.length;
    if (originalCount > 0 && originalCount < 16) {
      const neededCopies = Math.ceil(16 / originalCount);
      const originalCards = Array.from(track.children);
      for (let c = 1; c < neededCopies; c++) {
        originalCards.forEach((child) => {
          const clone = child.cloneNode(true);
          track.appendChild(clone);
        });
      }
    }

    // Ensure 360° rotating border beam exists on cards
    function ensureCardFrames() {
      Array.from(track.children).forEach((slide) => {
        const card = slide.querySelector('.zm-product-card');
        if (card && !card.querySelector('.zm-card-border-beam')) {
          const beam = document.createElement('div');
          beam.className = 'zm-card-border-beam';
          beam.setAttribute('aria-hidden', 'true');
          card.appendChild(beam);
        }
      });
    }
    ensureCardFrames();

    // Active leading card border glow
    function updateLeadingCard() {
      const allSlides = Array.from(track.children);
      allSlides.forEach((slide, idx) => {
        const card = slide.querySelector('.zm-product-card');
        if (!card) return;
        if (idx === 0) {
          card.classList.add('is-active-leading');
        } else {
          card.classList.remove('is-active-leading');
        }
      });
    }
    updateLeadingCard();

    // Loop state
    let isAdvancing = false;
    let isHovered = false;
    let isTouching = false;
    let isVisible = true;
    let isTabVisible = true;
    let rafId = null;
    let startTime = performance.now();
    let pausedElapsed = 0;
    const duration = parseInt(wrapper.dataset.progressDuration || '3000', 10);
    const animationDurationMs = 480;

    // Check if parent tab pane is visible
    function checkTabVisibility() {
      const parentPane = wrapper.closest('.zm-tab-pane');
      if (parentPane) {
        return parentPane.style.display !== 'none' && window.getComputedStyle(parentPane).display !== 'none';
      }
      return true;
    }

    // ===== SLIDE 1 BY 1 FORWARD (INFINITE MARQUEE LOOP) =====
    function advanceInfiniteSlide() {
      if (isAdvancing || !track.firstElementChild) return;
      isAdvancing = true;

      const step = getSingleStep();
      track.style.transition = `transform ${animationDurationMs}ms cubic-bezier(0.25, 1, 0.5, 1)`;
      track.style.transform = `translateX(-${step}px)`;

      setTimeout(() => {
        track.style.transition = 'none';
        const first = track.firstElementChild;
        if (first) {
          track.appendChild(first);
        }
        track.style.transform = 'translateX(0px)';
        void track.offsetWidth; // Force layout reflow
        isAdvancing = false;
        startTime = performance.now();
        pausedElapsed = 0;
        updateLeadingCard();
      }, animationDurationMs);
    }

    // ===== SLIDE 1 BY 1 REVERSE (PREV BUTTON) =====
    function prevInfiniteSlide() {
      if (isAdvancing || !track.lastElementChild) return;
      isAdvancing = true;

      const last = track.lastElementChild;
      track.insertBefore(last, track.firstElementChild);
      const step = getSingleStep();

      track.style.transition = 'none';
      track.style.transform = `translateX(-${step}px)`;
      void track.offsetWidth; // Force layout reflow

      track.style.transition = `transform ${animationDurationMs}ms cubic-bezier(0.25, 1, 0.5, 1)`;
      track.style.transform = 'translateX(0px)';

      setTimeout(() => {
        track.style.transition = 'none';
        isAdvancing = false;
        startTime = performance.now();
        pausedElapsed = 0;
        updateLeadingCard();
      }, animationDurationMs);
    }

    // ===== MARQUEE TICK LOOP =====
    function tick(now) {
      isTabVisible = checkTabVisibility();
      if (!isHovered && !isTouching && isVisible && isTabVisible && !isAdvancing) {
        const elapsed = now - startTime;
        if (elapsed >= duration) {
          advanceInfiniteSlide();
        }
      }
      rafId = requestAnimationFrame(tick);
    }
    rafId = requestAnimationFrame(tick);

    // Pause / Resume methods
    wrapper.__zmPause = function () {
      isTabVisible = false;
    };
    wrapper.__zmResume = function () {
      isTabVisible = true;
      startTime = performance.now();
      pausedElapsed = 0;
      updateLeadingCard();
    };

    // Hover pause and resume
    wrapper.addEventListener('mouseenter', () => {
      isHovered = true;
      pausedElapsed = performance.now() - startTime;
    });

    wrapper.addEventListener('mouseleave', () => {
      isHovered = false;
      startTime = performance.now() - pausedElapsed;
    });

    // ===== UNIFIED MOUSE SLIDE & TOUCH DRAG CONTROLLER =====
    let isDragging = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let dragCurrentX = 0;
    let dragDeltaX = 0;
    let isHorizontalGesture = false;
    let hasMoved = false;

    function handleStart(clientX, clientY) {
      if (isAdvancing) return;
      isDragging = true;
      hasMoved = false;
      dragStartX = clientX;
      dragStartY = clientY;
      dragCurrentX = clientX;
      dragDeltaX = 0;
      isHorizontalGesture = false;
      trackContainer.classList.add('is-dragging');
    }

    function handleMove(clientX, clientY) {
      if (!isDragging || isAdvancing) return;
      dragCurrentX = clientX;
      const dx = dragCurrentX - dragStartX;
      const dy = Math.abs(clientY - dragStartY);

      if (!isHorizontalGesture) {
        if (Math.abs(dx) > dy && Math.abs(dx) > 6) {
          isHorizontalGesture = true;
        }
      }

      if (isHorizontalGesture) {
        hasMoved = true;
        dragDeltaX = dx;
        track.style.transition = 'none';
        track.style.transform = `translateX(${dragDeltaX}px)`;
      }
    }

    function handleEnd() {
      if (!isDragging) return;
      isDragging = false;
      trackContainer.classList.remove('is-dragging');

      if (isHorizontalGesture && hasMoved) {
        if (dragDeltaX < -30) {
          advanceInfiniteSlide();
        } else if (dragDeltaX > 30) {
          prevInfiniteSlide();
        } else {
          track.style.transition = `transform 220ms cubic-bezier(0.25, 1, 0.5, 1)`;
          track.style.transform = 'translateX(0px)';
        }
      }

      startTime = performance.now();
      pausedElapsed = 0;
    }

    // Touch Listeners
    trackContainer.addEventListener('touchstart', (e) => {
      if (e.touches.length > 0) {
        handleStart(e.touches[0].clientX, e.touches[0].clientY);
      }
    }, { passive: true });

    trackContainer.addEventListener('touchmove', (e) => {
      if (e.touches.length > 0) {
        handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    }, { passive: true });

    trackContainer.addEventListener('touchend', handleEnd, { passive: true });
    trackContainer.addEventListener('touchcancel', handleEnd, { passive: true });

    // Mouse Drag Listeners (allows mouse slide on all screens & mobile emulation)
    trackContainer.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      handleStart(e.clientX, e.clientY);
    });

    window.addEventListener('mousemove', (e) => {
      if (isDragging) {
        handleMove(e.clientX, e.clientY);
      }
    });

    window.addEventListener('mouseup', (e) => {
      if (isDragging) {
        handleEnd();
      }
    });

    // Prevent accidental link navigation when dragging with mouse
    trackContainer.addEventListener('click', (e) => {
      if (hasMoved && Math.abs(dragDeltaX) > 8) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    // Arrow Button Handlers (1-by-1 Slide)
    if (nextBtn) {
      nextBtn.disabled = false;
      nextBtn.classList.remove('is-disabled');
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        advanceInfiniteSlide();
      });
    }

    if (prevBtn) {
      prevBtn.disabled = false;
      prevBtn.classList.remove('is-disabled');
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        prevInfiniteSlide();
      });
    }

    // Dots Container Handlers
    if (dotsContainer && originalCount > 1) {
      dotsContainer.innerHTML = '';
      const dotCount = Math.min(originalCount, 6);
      for (let i = 0; i < dotCount; i++) {
        const dot = document.createElement('button');
        dot.className = 'zm-carousel-dot' + (i === 0 ? ' active' : '');
        dot.setAttribute('aria-label', `Slide ${i + 1}`);
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          advanceInfiniteSlide();
        });
        dotsContainer.appendChild(dot);
      }
    }

    // Visibility Observer
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            isVisible = entry.isIntersecting;
            if (isVisible) {
              startTime = performance.now() - pausedElapsed;
            }
          });
        },
        { threshold: 0.05 }
      );
      observer.observe(wrapper);
    }
  }

  // ===== COUNTDOWN TIMER ENGINE WITH NUMBER TRANSITIONS =====
  function initCountdown(timerEl) {
    if (timerEl.__zmCountdownInit) return;
    timerEl.__zmCountdownInit = true;

    const endDateStr = timerEl.dataset.endDate;
    let targetDate;
    if (endDateStr) {
      targetDate = new Date(endDateStr).getTime();
    } else {
      targetDate = Date.now() + (178 * 24 * 60 * 60 * 1000) + (3 * 60 * 60 * 1000) + (14 * 60 * 1000);
    }

    const daysEl = timerEl.querySelector('.zm-days-val');
    const hoursEl = timerEl.querySelector('.zm-hours-val');
    const minsEl = timerEl.querySelector('.zm-mins-val');
    const secsEl = timerEl.querySelector('.zm-secs-val');

    function updateVal(el, newVal) {
      if (!el) return;
      const formatted = String(newVal).padStart(2, '0');
      if (el.textContent !== formatted) {
        el.textContent = formatted;
        el.classList.remove('digit-tick');
        void el.offsetWidth;
        el.classList.add('digit-tick');
      }
    }

    function tick() {
      const now = Date.now();
      const distance = targetDate - now;

      if (distance <= 0) {
        updateVal(daysEl, 0);
        updateVal(hoursEl, 0);
        updateVal(minsEl, 0);
        updateVal(secsEl, 0);
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % 1000) / 1000);

      updateVal(daysEl, days);
      updateVal(hoursEl, hours);
      updateVal(minsEl, minutes);
      updateVal(secsEl, seconds);
    }

    tick();
    setInterval(tick, 1000);
  }

  // ===== TAB SWITCHER ENGINE WITH SMOOTH TRANSITION =====
  function initTabs(section) {
    const tabBtns = section.querySelectorAll('.zm-tab-btn');
    const tabPanes = section.querySelectorAll('.zm-tab-pane');

    tabBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const targetId = btn.dataset.tabTarget;
        tabBtns.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');

        tabPanes.forEach((pane) => {
          const carousel = pane.querySelector('.zm-carousel-wrapper');
          if (pane.id === targetId) {
            pane.style.display = 'block';
            if (carousel) {
              if (!carousel.__zmCarouselInit) {
                initCarousel(carousel);
              } else if (typeof carousel.__zmResume === 'function') {
                carousel.__zmResume();
              }
            }
          } else {
            pane.style.display = 'none';
            if (carousel && typeof carousel.__zmPause === 'function') {
              carousel.__zmPause();
            }
          }
        });
      });
    });
  }

  // ===== MOBILE FOOTER ACCORDIONS =====
  function initFooterAccordions() {
    document.querySelectorAll('.zm-footer-col__title--accordion').forEach((title) => {
      title.addEventListener('click', () => {
        if (window.innerWidth <= 768) {
          title.parentElement.classList.toggle('active');
        }
      });
    });
  }

  // ===== GLOBAL INIT =====
  function initAll() {
    document.querySelectorAll('.zm-carousel-wrapper').forEach(initCarousel);
    document.querySelectorAll('.zm-countdown-timer').forEach(initCountdown);
    document.querySelectorAll('[data-zm-tabs]').forEach(initTabs);
    initFooterAccordions();
    initScrollReveal();
    initScrollProgressAndBackToTop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
